import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams, useHistory } from "react-router-dom";
import { FaEye, FaEdit, FaFileInvoiceDollar, FaMoneyBillWave, FaUser } from "react-icons/fa";
import axios from "axios";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";
import COLORS from "../resources/colors/COLORS";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import BackButton from "../ChildComponent/BackButton";
import MdlMonthlyPayRollDetails from "../dialog/MdlMonthlyPayRollDetails";
import MdlPayRollUpdatePayment from "../dialog/MdlPayRollUpdatePayment";
import {
  mdl,
  formatINR,
  PayrollHeaderChipBand,
  Section,
  Row,
  DaysAttendanceSection,
  SalaryRulesSection,
  CalculationsSection,
  SalaryComponentsSection,
  PayrollSummarySection,
  SalaryPaymentsSection,
  StaffPayrollHeaderChip,
  HeaderLockAction,
  HeaderSyncAttendanceAction,
  staffPayrollViewCells,
  payRollSalaryApiKey,
  payRollSalaryRecordMatches,
  computePayrollAmounts,
  buildPayrollCalculationItems,
  numOrZero,
} from "../dialog/payrollMonthlyShared";

const fontStack =
  'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

const ui = {
  pageBg: "#f5f7fb",
  surface: "#ffffff",
  border: "rgba(148, 163, 184, 0.32)",
  divider: "rgba(148, 163, 184, 0.22)",
  muted: "#64748b",
  text: "#0f172a",
  textSoft: "#334155",
  radiusXl: 20,
  radiusLg: 16,
  shadowLift: "0 1px 2px rgba(15, 23, 42, 0.05), 0 16px 48px rgba(15, 23, 42, 0.10)",
  heroGrad: `linear-gradient(120deg, ${COLORS.primary_color_shade9} 0%, ${COLORS.primary_color_shade7} 45%, ${COLORS.primary_color_shade5} 100%)`,
};

const ALERT_TONES = {
  warn: {
    background:
      "linear-gradient(135deg, rgba(245, 158, 11, 0.12) 0%, rgba(251, 191, 36, 0.18) 100%)",
    color: "#92400e",
    border: "1px solid rgba(245, 158, 11, 0.35)",
  },
  success: {
    background:
      "linear-gradient(135deg, rgba(236, 253, 245, 0.95) 0%, rgba(209, 250, 229, 0.55) 100%)",
    color: "#047857",
    border: "1px solid rgba(16, 185, 129, 0.35)",
  },
  error: {
    background:
      "linear-gradient(135deg, rgba(254, 226, 226, 0.9) 0%, rgba(254, 202, 202, 0.5) 100%)",
    color: "#b91c1c",
    border: "1px solid rgba(248, 113, 113, 0.45)",
  },
};

const pageShellStyle = {
  background: ui.pageBg,
  fontFamily: fontStack,
  color: ui.text,
  minHeight: "100%",
};

function AlertBanner({ tone, children }) {
  const t = ALERT_TONES[tone] || ALERT_TONES.warn;
  return (
    <div
      className="mb-3 py-2 px-3"
      style={{
        ...t,
        fontSize: 14,
        fontWeight: 600,
        borderRadius: mdl.radiusLg,
        fontFamily: mdl.font,
      }}
    >
      {children}
    </div>
  );
}

function parsePayMonth(yearMonth) {
  const ymParts = String(yearMonth || "").split("-");
  if (ymParts.length < 2) return null;
  const year = parseInt(ymParts[0], 10);
  const month = parseInt(ymParts[1], 10);
  if (Number.isNaN(year) || Number.isNaN(month)) return null;
  return { year, month };
}

const PayRollSalaryDetails = () => {
  const loggedIn = localStorage.getItem("token") !== null;
  const location = useLocation();
  const history = useHistory();
  const { salary_id: salaryIdParam } = useParams();

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const [staffRecord, setStaffRecord] = useState(
    () => location.state?.staffRecord ?? null
  );
  const [pageLoading, setPageLoading] = useState(!location.state?.staffRecord);
  const [pageError, setPageError] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [lockBusy, setLockBusy] = useState(false);
  const [syncAttendanceBusy, setSyncAttendanceBusy] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [lockMessage, setLockMessage] = useState("");
  const [salaryComponents, setSalaryComponents] = useState(null);
  const [salaryComponentsLoading, setSalaryComponentsLoading] = useState(false);
  const [salaryComponentsError, setSalaryComponentsError] = useState("");
  const [salaryPayments, setSalaryPayments] = useState([]);
  const [paymentSummary, setPaymentSummary] = useState(null);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [paymentsError, setPaymentsError] = useState("");
  const [paymentSaving, setPaymentSaving] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentSaveError, setPaymentSaveError] = useState("");

  const sessionId = useMemo(
    () =>
      staffRecord?.session_id ??
      location.state?.sessionId ??
      searchParams.get("session_id"),
    [staffRecord?.session_id, location.state?.sessionId, searchParams]
  );

  const yearMonth = useMemo(
    () =>
      staffRecord?.year_month ??
      location.state?.yearMonth ??
      searchParams.get("year_month"),
    [staffRecord?.year_month, location.state?.yearMonth, searchParams]
  );

  const isRecordLocked = staffRecord ? Number(staffRecord.is_locked) === 1 : false;
  const busy = lockBusy || syncAttendanceBusy || paymentSaving;

  const openPaymentModal = useCallback(() => {
    setPaymentSaveError("");
    setPaymentModalOpen(true);
  }, []);

  const canRecordPayment = useMemo(() => {
    if (!staffRecord || isRecordLocked) return false;
    const status = String(staffRecord.status_code || "").toLowerCase();
    return status !== "cancelled" && status !== "reversed";
  }, [staffRecord, isRecordLocked]);

  const loadSalaryComponentsFor = useCallback(async (record) => {
    if (!payRollSalaryApiKey(record) || !record?.staff_id || !record?.year_month) {
      setSalaryComponents(null);
      setSalaryComponentsError("");
      setSalaryComponentsLoading(false);
      return;
    }
    const URL = `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}getPayRollMonthlyStaffSalaryComponents.php`;
    setSalaryComponentsLoading(true);
    setSalaryComponentsError("");
    try {
      const schoolCode = record.school_code || SessionManager.getSchoolCode();
      const res = await axios.get(URL, {
        params: {
          salary_id: payRollSalaryApiKey(record),
          staff_id: String(record.staff_id),
          year_month: String(record.year_month),
          school_code: schoolCode,
          session_id: record.session_id != null ? String(record.session_id) : undefined,
        },
      });
      if (res.data?.api_status === "OK") {
        setSalaryComponents({
          earnings: res.data.earnings || [],
          deductions: res.data.deductions || [],
          total_earnings: res.data.total_earnings,
          total_deductions: res.data.total_deductions,
          component_master: res.data.component_master || [],
          staff_components: res.data.staff_components || [],
        });
      } else {
        setSalaryComponents(null);
        setSalaryComponentsError(
          (res.data && res.data.api_remark) || "Could not load salary components."
        );
      }
    } catch (e) {
      setSalaryComponents(null);
      setSalaryComponentsError(
        e?.response?.data?.api_remark ||
          e?.message ||
          "Network error while loading salary components."
      );
    } finally {
      setSalaryComponentsLoading(false);
    }
  }, []);

  const loadSalaryPaymentsFor = useCallback(async (record) => {
    if (!payRollSalaryApiKey(record) || !record?.session_id) {
      setSalaryPayments([]);
      setPaymentSummary(null);
      setPaymentsError("");
      setPaymentsLoading(false);
      return;
    }
    const schoolCode = record.school_code || SessionManager.getSchoolCode();
    const URL = `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}getPayRollSalaryPayments.php`;
    setPaymentsLoading(true);
    setPaymentsError("");
    try {
      const res = await axios.get(URL, {
        params: {
          school_code: schoolCode,
          session_id: String(record.session_id),
          salary_id: payRollSalaryApiKey(record),
          staff_id: String(record.staff_id || ""),
        },
      });
      if (res.data?.api_status === "OK") {
        setSalaryPayments(res.data.payments || []);
        setPaymentSummary(res.data.summary || null);
        setStaffRecord((prev) =>
          prev && String(prev.id) === String(record.id)
            ? {
                ...prev,
                total_paid: res.data.summary?.total_paid,
                balance_due: res.data.summary?.balance_due,
                payment_date: res.data.summary?.payment_date,
                status_code: res.data.summary?.status_code ?? prev.status_code,
              }
            : prev
        );
      } else {
        setSalaryPayments([]);
        setPaymentSummary(null);
        setPaymentsError(
          (res.data && res.data.api_remark) || "Could not load salary payments."
        );
      }
    } catch (e) {
      setSalaryPayments([]);
      setPaymentSummary(null);
      setPaymentsError(
        e?.response?.data?.api_remark ||
          e?.message ||
          "Network error while loading salary payments."
      );
    } finally {
      setPaymentsLoading(false);
    }
  }, []);

  const fetchStaffRecord = useCallback(async () => {
    if (!salaryIdParam || !sessionId || !yearMonth) {
      setPageError("Missing payroll record identifiers. Open this page from the payroll list.");
      setPageLoading(false);
      return null;
    }
    const schoolCode = SessionManager.getSchoolCode();
    const parsed = parsePayMonth(yearMonth);
    if (!schoolCode || !parsed) {
      setPageError(parsed ? "Could not resolve school or pay month." : "Invalid pay month.");
      setPageLoading(false);
      return null;
    }

    const URL = `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}getPayRollMonthlySalary.php`;
    try {
      const res = await axios.get(URL, {
        params: {
          school_code: schoolCode,
          session_id: String(sessionId),
          month: parsed.month,
          year: parsed.year,
        },
      });
      if (res.data?.api_status !== "OK") {
        setPageError(
          (res.data && res.data.api_remark) || "Could not load payroll record."
        );
        return null;
      }
      const row = (res.data.staff_payroll || []).find((item) =>
        payRollSalaryRecordMatches(item, salaryIdParam)
      );
      if (!row) {
        setPageError("Payroll record not found for this month.");
        return null;
      }
      setStaffRecord(row);
      setPageError("");
      return row;
    } catch (e) {
      setPageError(
        e?.response?.data?.api_remark ||
          e?.message ||
          "Network error while loading payroll record."
      );
      return null;
    } finally {
      setPageLoading(false);
    }
  }, [salaryIdParam, sessionId, yearMonth]);

  const reloadPayrollData = useCallback(async () => {
    const row = await fetchStaffRecord();
    if (row) {
      await Promise.all([
        loadSalaryComponentsFor(row),
        loadSalaryPaymentsFor(row),
      ]);
    }
    return row;
  }, [fetchStaffRecord, loadSalaryComponentsFor, loadSalaryPaymentsFor]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const initial = location.state?.staffRecord;
      if (initial) {
        if (!cancelled) {
          setPageLoading(false);
          await Promise.all([
            loadSalaryComponentsFor(initial),
            loadSalaryPaymentsFor(initial),
          ]);
        }
        return;
      }
      const row = await fetchStaffRecord();
      if (!cancelled && row) {
        await Promise.all([
          loadSalaryComponentsFor(row),
          loadSalaryPaymentsFor(row),
        ]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [location.state?.staffRecord, fetchStaffRecord, loadSalaryComponentsFor, loadSalaryPaymentsFor]);

  const payrollAmounts = useMemo(() => {
    if (!staffRecord) return null;
    return computePayrollAmounts(staffRecord, null, false, salaryComponents);
  }, [staffRecord, salaryComponents]);

  const derivedNet = payrollAmounts?.netSalary ?? 0;

  const calculationDisplay = useMemo(() => {
    if (!staffRecord) return null;
    return buildPayrollCalculationItems(
      staffRecord,
      null,
      false,
      salaryComponents,
      payrollAmounts
    );
  }, [staffRecord, salaryComponents, payrollAmounts]);

  const salarySummary = useMemo(() => {
    if (!staffRecord) return null;
    return {
      gross:
        payrollAmounts?.grossEarnings ??
        staffRecord.gross_earnings ??
        staffRecord.basic_salary,
      deductions: staffRecord.total_deductions ?? staffRecord.deductions ?? 0,
      showRoundedNet:
        staffRecord.rounded_net != null &&
        String(staffRecord.rounded_net) !== "" &&
        Number(staffRecord.rounded_net) !== Number(staffRecord.net_salary),
    };
  }, [staffRecord, payrollAmounts]);

  const patchStaffRecord = useCallback((patch) => {
    setStaffRecord((prev) => (prev ? { ...prev, ...patch } : prev));
  }, []);

  const postLockStatus = useCallback(
    async (nextLocked) => {
      if (!staffRecord) return { ok: false, remark: "No record loaded." };
      const schoolCode =
        staffRecord.school_code || SessionManager.getSchoolCode();
      const sessionIdVal = staffRecord.session_id;
      if (!schoolCode || sessionIdVal == null || !payRollSalaryApiKey(staffRecord)) {
        return { ok: false, remark: "Missing school, session, or record id." };
      }
      const URL = `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}setPayRollMonthlyStaffSalaryLock.php`;
      const body = new URLSearchParams();
      body.append("school_code", String(schoolCode));
      body.append("session_id", String(sessionIdVal));
      body.append("salary_id", payRollSalaryApiKey(staffRecord));
      body.append("is_locked", nextLocked ? "1" : "0");
      try {
        const res = await axios.post(URL, body.toString(), {
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
        });
        if (res.data?.api_status === "OK") {
          return {
            ok: true,
            remark:
              res.data.api_remark ||
              (nextLocked ? "Record locked." : "Record unlocked."),
          };
        }
        return {
          ok: false,
          remark: (res.data && res.data.api_remark) || "Lock update was not accepted.",
        };
      } catch (e) {
        return {
          ok: false,
          remark:
            e?.response?.data?.api_remark ||
            e?.message ||
            "Network error while updating lock.",
        };
      }
    },
    [staffRecord]
  );

  const handleSetLocked = useCallback(
    async (nextLocked) => {
      if (nextLocked === isRecordLocked) return;
      setLockBusy(true);
      setSaveError("");
      setLockMessage("");
      const result = await postLockStatus(nextLocked);
      setLockBusy(false);
      if (result.ok) {
        const msg =
          result.remark || (nextLocked ? "Record locked." : "Record unlocked.");
        setLockMessage(msg);
        myToast.notify_success(msg);
        await reloadPayrollData();
      } else {
        setSaveError(result.remark);
      }
    },
    [isRecordLocked, postLockStatus, reloadPayrollData]
  );

  const handleSyncAttendance = useCallback(async () => {
    if (!staffRecord || isRecordLocked) return;
    const schoolCode =
      staffRecord.school_code || SessionManager.getSchoolCode();
    const sessionIdVal = staffRecord.session_id;
    if (!schoolCode || sessionIdVal == null || !payRollSalaryApiKey(staffRecord)) {
      setSaveError("Missing school, session, or record id.");
      return;
    }
    setSyncAttendanceBusy(true);
    setSaveError("");
    setLockMessage("");
    const URL = `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}syncPayRollMonthlyStaffAttendance.php`;
    const body = new URLSearchParams();
    body.append("school_code", String(schoolCode));
    body.append("session_id", String(sessionIdVal));
    body.append("salary_id", payRollSalaryApiKey(staffRecord));
    try {
      const res = await axios.post(URL, body.toString(), {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      });
      if (res.data?.api_status === "OK" && res.data.attendance) {
        const att = res.data.attendance;
        patchStaffRecord({
          staff_present: numOrZero(att.staff_present),
          staff_absent: numOrZero(att.staff_absent),
          staff_leave: numOrZero(att.staff_leave),
          paid_leave: numOrZero(att.paid_leave),
          half_day: numOrZero(att.half_day),
          late_day: numOrZero(att.late_day),
          is_fetch_attendance: 1,
        });
        const msg =
          res.data.api_remark ||
          "Attendance loaded from staff attendance. Use Edit and save to store in payroll.";
        myToast.notify_success(msg);
        setLockMessage(msg);
      } else {
        setSaveError(
          (res.data && res.data.api_remark) ||
            "Could not load attendance from staff attendance."
        );
      }
    } catch (e) {
      setSaveError(
        e?.response?.data?.api_remark ||
          e?.message ||
          "Network error while syncing attendance."
      );
    } finally {
      setSyncAttendanceBusy(false);
    }
  }, [staffRecord, isRecordLocked, patchStaffRecord]);

  const handleEditSaved = useCallback(() => {
    reloadPayrollData();
  }, [reloadPayrollData]);

  const handleEditPatched = useCallback((updated) => {
    if (updated && typeof updated === "object") {
      setStaffRecord(updated);
    }
  }, []);

  const handleSavePayment = useCallback(
    async (payment) => {
      if (!staffRecord || isRecordLocked) return;
      const schoolCode =
        staffRecord.school_code || SessionManager.getSchoolCode();
      if (!schoolCode || staffRecord.session_id == null || !payRollSalaryApiKey(staffRecord)) {
        setPaymentSaveError("Missing school, session, or record id.");
        return;
      }
      setPaymentSaving(true);
      setPaymentSaveError("");
      const isUpdate = payment.payment_id != null && payment.payment_id !== "";
      const endpoint = isUpdate
        ? "updatePayRollSalaryPayment.php"
        : "addPayRollSalaryPayment.php";
      const URL = `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}${endpoint}`;
      const body = new URLSearchParams();
      body.append("school_code", String(schoolCode));
      body.append("session_id", String(staffRecord.session_id));
      body.append("salary_id", payRollSalaryApiKey(staffRecord));
      body.append("staff_id", String(staffRecord.staff_id ?? "").trim());
      const payType = String(payment.payment_type || "PART").trim().toUpperCase();
      body.append("payment_type", ["ADVANCE", "PART", "FINAL"].includes(payType) ? payType : "PART");
      const modeRaw = String(payment.payment_mode || "").trim().toUpperCase();
      body.append("payment_mode", modeRaw || "CASH");
      body.append("amount", String(payment.amount));
      body.append("payment_date", String(payment.payment_date || "").trim());
      if (isUpdate) {
        body.append("payment_id", String(payment.payment_id));
      }
      if (payment.transaction_ref) {
        body.append("transaction_ref", payment.transaction_ref);
      }
      if (payment.remarks) {
        body.append("remarks", payment.remarks);
      }
      try {
        const res = await axios.post(URL, body.toString(), {
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
        });
        if (res.data?.api_status === "OK") {
          myToast.notify_success(
            res.data.api_remark ||
              (isUpdate ? "Payment updated successfully." : "Payment recorded successfully.")
          );
          setLockMessage(res.data.api_remark || "");
          setPaymentModalOpen(false);
          setPaymentSaveError("");
          await reloadPayrollData();
        } else {
          setPaymentSaveError(
            (res.data && res.data.api_remark) ||
              (isUpdate ? "Could not update payment." : "Could not record payment.")
          );
        }
      } catch (e) {
        setPaymentSaveError(
          e?.response?.data?.api_remark ||
            e?.message ||
            (isUpdate
              ? "Network error while updating payment."
              : "Network error while recording payment.")
        );
      } finally {
        setPaymentSaving(false);
      }
    },
    [staffRecord, isRecordLocked, reloadPayrollData]
  );

  const handleGetPayslip = useCallback(() => {
    const salaryKey = payRollSalaryApiKey(staffRecord);
    if (!salaryKey || staffRecord?.session_id == null) {
      myToast.notify_warning("Payroll record is not ready for payslip.");
      return;
    }
    history.push(
      `/AdminMain/PayRollPayslip/${encodeURIComponent(salaryKey)}/${encodeURIComponent(
        staffRecord.session_id
      )}`
    );
  }, [staffRecord, history]);

  //Created By Cursor
  const handleOpenStaffDetails = useCallback(() => {
    const staffId = staffRecord?.staff_id;
    if (!staffId) {
      myToast.notify_warning("Staff id is missing.");
      return;
    }
    const sid = staffRecord?.session_id ?? sessionId;
    history.push({
      pathname: `/AdminMain/PayRollStaffDetail/${encodeURIComponent(
        String(staffId)
      )}`,
      search: sid ? `?session_id=${encodeURIComponent(String(sid))}` : "",
    });
  }, [staffRecord, sessionId, history]);

  const headerChips = useMemo(() => {
    if (!staffRecord) return null;
    return staffPayrollViewCells(staffRecord).flatMap((cell) => {
      const chip = (
        <StaffPayrollHeaderChip
          key={cell.label}
          label={cell.label}
          value={cell.value}
          title={cell.title}
        />
      );
      if (cell.label === "Locked") {
        return [
          chip,
          <HeaderLockAction
            key="lock-action"
            isLocked={isRecordLocked}
            onLock={() => handleSetLocked(true)}
            onUnlock={() => handleSetLocked(false)}
            busy={busy}
            saving={false}
          />,
          <HeaderSyncAttendanceAction
            key="sync-attendance"
            onSync={handleSyncAttendance}
            busy={syncAttendanceBusy}
            disabled={isRecordLocked}
          />,
        ];
      }
      return [chip];
    });
  }, [
    staffRecord,
    isRecordLocked,
    busy,
    syncAttendanceBusy,
    handleSetLocked,
    handleSyncAttendance,
  ]);

  if (!loggedIn) {
    return (
      <div className="container mt-5">
        <div className="row justify-content-center">
          <div className="col-md-6 text-center">
            <h2>Access Denied</h2>
            <p>You must be logged in to view this page.</p>
            <Link to="/Login" className="btn btn-primary">
              Go to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (pageLoading) {
    return (
      <div
        className="content payroll-page d-flex align-items-center justify-content-center"
        style={{ minHeight: "60vh", background: ui.pageBg, fontFamily: fontStack }}
      >
        <div className="text-center" style={{ color: ui.muted, fontWeight: 600 }}>
          Loading payroll details…
        </div>
      </div>
    );
  }

  if (pageError || !staffRecord || !salarySummary) {
    return (
      <div className="content payroll-page" style={pageShellStyle}>
        <div
          className="content-header d-flex justify-content-between align-items-center"
          style={{ borderBottom: `1px solid ${ui.divider}`, paddingBottom: 6 }}
        >
          <span style={{ fontWeight: 800, fontSize: 16, color: ui.text }}>
            Monthly payroll
          </span>
          <BackButton />
        </div>
        <div className="animated fadeInUp" style={{ margin: 0, padding: 0 }}>
          <div style={{ padding: "16px 0" }}>
            <AlertBanner tone="error">
              {pageError || "Payroll record could not be loaded."}
            </AlertBanner>
            <Link to="/AdminMain/PayRoll" className="btn btn-primary">
              Back to payroll list
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const r = staffRecord;
  const { gross, deductions, showRoundedNet } = salarySummary;

  return (
    <div className="content payroll-page" style={pageShellStyle}>
      <div
        className="content-header d-flex justify-content-between align-items-center"
        style={{ borderBottom: `1px solid ${ui.divider}`, paddingBottom: 6 }}
      >
        <div className="leftside-content-header d-flex align-items-center gap-2">
          <span
            aria-hidden
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(3, 169, 244, 0.12)",
              color: COLORS.primary_color_shade6,
            }}
          >
            <FaEye />
          </span>
          <span style={{ fontWeight: 800, fontSize: 16, color: ui.text }}>
            Monthly payroll (view)
          </span>
        </div>
        <div className="rightside-content-header d-flex align-items-center">
          <BackButton />
        </div>
      </div>

      <div className="animated fadeInUp" style={{ margin: 0, padding: 0, width: "100%" }}>
        <div
          className="payroll-table-container"
          style={{
            background: ui.surface,
            borderRadius: ui.radiusXl,
            border: `1px solid ${ui.border}`,
            boxShadow: ui.shadowLift,
            overflow: "hidden",
            width: "100%",
          }}
        >
          <PayrollHeaderChipBand>{headerChips}</PayrollHeaderChipBand>
          <div style={{ padding: "16px 20px 24px", background: ui.surface }}>
            {isRecordLocked ? (
              <AlertBanner tone="warn">
                This record is locked. Use Unlock record to edit payroll fields.
              </AlertBanner>
            ) : null}
            {lockMessage ? (
              <AlertBanner tone="success">{lockMessage}</AlertBanner>
            ) : null}
            {saveError ? <AlertBanner tone="error">{saveError}</AlertBanner> : null}

            <Section title="Salary">
              <Row label="Basic" value={formatINR(r.basic_salary)} entityTone="basic" />
              <Row label="Gross earnings" value={formatINR(gross)} entityTone="gross" />
              <Row label="Allowances" value={formatINR(r.allowances)} entityTone="allowances" />
              <Row label="Deductions" value={formatINR(deductions)} entityTone="deductions" />
              <Row label="Net salary" value={formatINR(r.net_salary)} entityTone="netSalary" />
              {showRoundedNet ? (
                <Row
                  label="Rounded net"
                  value={formatINR(r.rounded_net)}
                  entityTone="roundedNet"
                />
              ) : null}
            </Section>

            <DaysAttendanceSection record={r} isEdit={false} />

            <SalaryRulesSection
              rules={r.salary_rules}
              isEdit={false}
              prorataValue={r.salary_rules?.prorata_basis}
            />

            <CalculationsSection calculationDisplay={calculationDisplay} />

            <SalaryComponentsSection
              bundle={salaryComponents}
              loading={salaryComponentsLoading}
              error={salaryComponentsError}
              isEdit={false}
              disabled
            />

            <PayrollSummarySection
              payrollAmounts={payrollAmounts}
              record={r}
              isEdit={false}
              derivedNet={derivedNet}
            />

            <SalaryPaymentsSection
              record={r}
              payments={salaryPayments}
              summary={paymentSummary}
              loading={paymentsLoading}
              error={paymentsError}
              isLocked={isRecordLocked}
            />
          </div>

          <div
            className="d-flex flex-wrap justify-content-end gap-2 px-3 py-3"
            style={{
              borderTop: `1px solid ${ui.border}`,
              background: mdl.footerBg || "#f8fafc",
            }}
          >
            <button
              type="button"
              className="btn fw-semibold text-white payroll-cta lift"
              onClick={handleOpenStaffDetails}
              disabled={busy || !staffRecord?.staff_id}
              title="Open staff salary dashboard"
              style={{
                borderRadius: 12,
                padding: "10px 22px",
                minWidth: 140,
              }}
            >
              <FaUser className="me-2" />
              Staff Salary Dashboard
            </button>
            <button
              type="button"
              className="btn fw-semibold text-white payroll-cta lift"
              onClick={() => setEditOpen(true)}
              disabled={busy}
              style={{
                borderRadius: 12,
                padding: "10px 22px",
                minWidth: 140,
              }}
            >
              <FaEdit className="me-2" />
              Edit Salary
            </button>
            <button
              type="button"
              className="btn fw-semibold text-white payroll-cta lift"
              onClick={openPaymentModal}
              disabled={busy || !canRecordPayment}
              title={
                isRecordLocked
                  ? "Unlock the record to record a payment"
                  : !canRecordPayment
                    ? "Payments cannot be recorded for this payroll status"
                    : "Record or update salary payment"
              }
              style={{
                borderRadius: 12,
                padding: "10px 22px",
                minWidth: 160,
              }}
            >
              <FaMoneyBillWave className="me-2" />
              Record Payment
            </button>
            <button
              type="button"
              className="btn fw-semibold text-white payroll-cta-receipt lift"
              onClick={handleGetPayslip}
              disabled={busy}
              style={{
                borderRadius: 12,
                padding: "10px 20px",
                minWidth: 140,
              }}
            >
              <FaFileInvoiceDollar className="me-2" />
              Get Payslip
            </button>
          </div>
        </div>
      </div>

      {editOpen ? (
        <MdlMonthlyPayRollDetails
          show={editOpen}
          actionClose={() => setEditOpen(false)}
          staffRecord={staffRecord}
          onSaved={handleEditSaved}
          onRecordPatched={handleEditPatched}
        />
      ) : null}

      {paymentModalOpen ? (
        <MdlPayRollUpdatePayment
          show={paymentModalOpen}
          actionClose={() => {
            setPaymentModalOpen(false);
            setPaymentSaveError("");
          }}
          staffRecord={staffRecord}
          payments={salaryPayments}
          summary={paymentSummary}
          saving={paymentSaving}
          saveError={paymentSaveError}
          onSave={handleSavePayment}
        />
      ) : null}
    </div>
  );
};

export default PayRollSalaryDetails;
